import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { StepRoute } from '../../../../i18n/pathnames/licenses';
import { BusinessInformation } from './(steps)/business-information/forms/types/businessInformation';
import { bootstrapLicense } from './(steps)/config/bootstrap.shared';
import {
  CustomerInformation,
  defaultCustomerInformation,
} from './(steps)/customer-information/customerInformation.schema';
import { defaultPaymentInformation, PaymentInformation } from './(steps)/summary-payment/summaryPayment.schema';
import { LicenseCode, LicenseInformation, LicensePlan, SanityLicense } from './types/licenses';

type LicenseStoreInfo = Pick<SanityLicense, 'wareNumbers' | 'categories'>;

export interface LicenseStore {
  customerInformation: CustomerInformation;
  paymentInformation: PaymentInformation;
  businessInformation?: BusinessInformation;
  licenseInformation?: LicenseInformation;
  licensePlan?: LicensePlan;
  stepValidity: Partial<Record<StepRoute, boolean>>;
  setStepValidity: (route: StepRoute, isValid: boolean) => void;
  setCurrentLicense: (licenseCode: LicenseCode, license: LicenseStoreInfo) => void;
  updateCustomerInformation: (partial: Partial<CustomerInformation>) => void;
  updatePaymentInformation: (partial: Partial<PaymentInformation>) => void;
  updateLicenseForm: (partial: Partial<BusinessInformation>) => void;
  resetLicenseForm: () => void;
  resetLicensePlan: () => void;
  softReset: () => void;
  updateLicensePlan: (licensePlan: LicensePlan) => void;
  // UI helpers
  showErrors: boolean;
  setShowErrors: (show: boolean) => void;
  isHydrated: boolean;
  setIsHydrated: () => void;
  reset: () => void;
}

export const useLicenseStore = create<LicenseStore>()(
  persist(
    set => ({
      customerInformation: defaultCustomerInformation,
      paymentInformation: defaultPaymentInformation,
      businessInformation: undefined,
      licensePlan: undefined,
      stepValidity: {},
      setStepValidity: (route, isValid) =>
        set(state => {
          const prev = state.stepValidity?.[route];
          if (prev === isValid) return state;
          return {
            stepValidity: {
              ...(state.stepValidity ?? {}),
              [route]: isValid,
            },
          };
        }),
      setCurrentLicense: (licenseCode, license) =>
        set(state => {
          // Only bootstrap if switching to a new license or no form exists
          const shouldBootstrap =
            state.businessInformation?.licenseCode !== licenseCode || state.businessInformation === undefined;
          return {
            // `showErrors` is UI state: bootstrapping a new license should never start by showing errors.
            // Preserve existing `showErrors` only when we keep the same businessInformation.
            showErrors: shouldBootstrap ? false : state.showErrors,
            businessInformation: shouldBootstrap ? bootstrapLicense(licenseCode) : state.businessInformation,
            licensePlan: shouldBootstrap ? undefined : state.licensePlan,
            stepValidity: shouldBootstrap ? {} : state.stepValidity,
            licenseInformation: {
              wareNumbers: license.wareNumbers,
              licenseCode: licenseCode,
              categories: license.categories,
            },
          };
        }),
      updateCustomerInformation: partial =>
        set(state => ({ customerInformation: { ...state.customerInformation, ...partial } })),
      updatePaymentInformation: partial =>
        set(state => ({ paymentInformation: { ...state.paymentInformation, ...partial } })),
      updateLicenseForm: partial =>
        set(state => {
          if (state.businessInformation) {
            return {
              businessInformation: {
                ...state.businessInformation,
                ...partial,
              },
            };
          }

          const licenseCode = partial.licenseCode;
          if (!licenseCode) return {};
          const bootstrapped = bootstrapLicense(licenseCode);
          return {
            businessInformation: {
              ...bootstrapped,
              ...partial,
            },
          };
        }),
      updateLicensePlan: licensePlan => {
        set(() => ({
          licensePlan: {
            ...licensePlan,
          },
        }));
      },
      resetLicenseForm: () =>
        set(() => ({
          businessInformation: undefined,
          stepValidity: {},
        })),
      resetLicensePlan: () =>
        set(() => ({
          licensePlan: undefined,
        })),
      softReset: () =>
        set(() => ({
          customerInformation: defaultCustomerInformation,
          paymentInformation: defaultPaymentInformation,
          businessInformation: undefined,
          stepValidity: {},
          showErrors: false,
        })),
      setShowErrors: show => set({ showErrors: show }),
      showErrors: false,
      isHydrated: false,
      setIsHydrated: () => set({ isHydrated: true }),
      reset: () =>
        set({
          licensePlan: undefined,
          customerInformation: defaultCustomerInformation,
          paymentInformation: defaultPaymentInformation,
          businessInformation: undefined,
          stepValidity: {},
          showErrors: false,
        }),
    }),
    {
      name: 'license-store',
      version: 1,
      onRehydrateStorage: () => state => state?.setIsHydrated(),
      migrate: persisted => persisted,
      partialize: state => ({
        customerInformation: state.customerInformation,
        paymentInformation: state.paymentInformation,
        businessInformation: state.businessInformation,
        licenseInformation: state.licenseInformation,
        licensePlan: state.licensePlan,
        stepValidity: state.stepValidity,
      }),
      storage: createJSONStorage(() => sessionStorage), // store in session storage to avoid persisting sensitive data
    },
  ),
);
